/**
 * 上传内容：选类型 → 模块编辑器（互动游戏是游戏库 → 设置）→ 标题 / 文件夹 / 标签 / 预计用时 → POST contents（与 iOS UploadFlowView 一致）。
 * 可选的类型 = 注册表里 creatable 的、并且网页版登记了编辑器的（modules.tsx）。
 */
import { ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { type JSONObject, run, useStore, writeEntity } from "../../core";
import { BarProgress, C, ChoiceChip, FormCard, ModalHeader, SectionLabel, Sheet, SheetBody, SheetHeader, Spinner, TextField, ValueRow } from "../../ui";
import { ACTIVITIES } from "../../activities/registry";
import { sortFolders, sortTags } from "./activity";
import { type EditorMeta, useUploader } from "./editors";
import { TypeBlock } from "./Library";
import { webModule } from "./modules";

type Step = { kind: "type" } | { kind: "editor"; type: string } | { kind: "meta"; type: string; payload: JSONObject; version: number; meta: EditorMeta };

export function UploadSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<Step>({ kind: "type" });
  const uploader = useUploader();
  const [saving, setSaving] = useState(false);
  const locked = uploader.uploading || saving;

  const close = () => {
    if (locked) return; // 上传中不能关闭
    onClose();
    setStep({ kind: "type" });
  };

  const types = ACTIVITIES.filter((a) => a.creatable && webModule(a.type).Editor);
  const defaultTitle = (type: string, payload: JSONObject, meta: EditorMeta): string =>
    meta.title ?? webModule(type).defaultTitle?.(payload, uploader.lastFileName) ?? "";

  let body: ReactNode;
  if (step.kind === "type") {
    body = (
      <>
        <SheetHeader title="添加学习内容" onClose={close} />
        <SheetBody gap={10}>
          {types.map((o) => (
            <button key={o.type} type="button" className="learn-type-option" onClick={() => setStep({ kind: "editor", type: o.type })}>
              <TypeBlock type={o.type} width={48} height={48} radius={14} size={13} />
              <span className="learn-row-main">
                <strong>{o.name}</strong>
                <span>{o.description}</span>
              </span>
              <ChevronRight size={16} strokeWidth={2.6} color={C.inkSoft} />
            </button>
          ))}
        </SheetBody>
      </>
    );
  } else if (step.kind === "editor") {
    const Editor = webModule(step.type).Editor!;
    body = <Editor uploader={uploader} onCancel={() => !locked && setStep({ kind: "type" })} onSave={(payload, version, meta = {}) => setStep({ kind: "meta", type: step.type, payload, version, meta })} />;
  } else {
    body = (
      <ContentMetaForm
        type={step.type}
        payload={step.payload}
        version={step.version}
        defaultTitle={defaultTitle(step.type, step.payload, step.meta)}
        defaultMinutes={step.meta.minutes ?? 0}
        onBusy={setSaving}
        onBack={() => setStep({ kind: "type" })}
        onDone={() => {
          onClose();
          setStep({ kind: "type" });
        }}
      />
    );
  }

  return (
    <Sheet open={open} onClose={close} persistent={locked || step.kind !== "type"} width={560} label="添加学习内容">
      <div className="learn-flow">
        {body}
        {uploader.uploading && (
          <div className="learn-upload-overlay" role="status" aria-live="polite">
            <div>
              <Spinner size={26} />
              <span>{uploader.stage}</span>
              <BarProgress value={uploader.progress} height={8} track="var(--kp-dark3)" fill="var(--kp-accent)" />
              <small className="kp-num">{Math.round(uploader.progress * 100)}%</small>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 30, 45, 60];

/** 标题、文件夹、标签、预计用时（家长端只能选已有的文件夹和标签） */
function ContentMetaForm({
  type,
  payload,
  version,
  defaultTitle,
  defaultMinutes,
  onBusy,
  onBack,
  onDone,
}: {
  type: string;
  payload: JSONObject;
  version: number;
  defaultTitle: string;
  defaultMinutes: number;
  onBusy: (b: boolean) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const foldersById = useStore((s) => s.folders);
  const tagsById = useStore((s) => s.tags);
  const folders = sortFolders(Object.values(foldersById));
  const tags = sortTags(Object.values(tagsById));
  const [title, setTitle] = useState(defaultTitle);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    onBusy(true);
    const c = await run(
      () =>
        writeEntity("contents", "POST", "contents", {
          folderId,
          tagIds,
          title: title.trim(),
          activityType: type,
          payload,
          payloadVersion: version,
          estimatedMin: minutes || undefined,
        }),
      "已添加到内容库",
    );
    setBusy(false);
    onBusy(false);
    if (c) onDone();
  };

  return (
    <>
      <ModalHeader title="保存到内容库" cancelTitle="放弃" actionTitle="保存" actionEnabled={!!title.trim()} busy={busy} onCancel={onBack} onAction={() => void save()} />
      <SheetBody gap={12}>
        <TextField label="标题" value={title} onChange={setTitle} placeholder="例如：一年级上册练习纸" data-testid="content-title" />
        <SectionLabel>文件夹</SectionLabel>
        <div className="learn-chips">
          <ChoiceChip selected={folderId == null} unselectedBg={C.surface} onClick={() => setFolderId(null)}>
            不放文件夹
          </ChoiceChip>
          {folders.map((f) => (
            <ChoiceChip key={f.id} selected={folderId === f.id} unselectedBg={C.surface} onClick={() => setFolderId(f.id)}>
              {f.name}
            </ChoiceChip>
          ))}
        </div>
        <SectionLabel>标签（可多选）</SectionLabel>
        <div className="learn-chips">
          {tags.length === 0 && <span className="learn-note">还没有标签</span>}
          {tags.map((t) => (
            <ChoiceChip key={t.id} selected={tagIds.includes(t.id)} unselectedBg={C.surface} onClick={() => setTagIds((l) => (l.includes(t.id) ? l.filter((x) => x !== t.id) : [...l, t.id]))}>
              #{t.name}
            </ChoiceChip>
          ))}
        </div>
        <FormCard>
          <ValueRow title="预计用时">
            <select className="learn-select" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} aria-label="预计用时">
              {(MINUTE_OPTIONS.includes(defaultMinutes) ? MINUTE_OPTIONS : [...MINUTE_OPTIONS, defaultMinutes].sort((a, b) => a - b)).map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? "不填" : `${m} 分钟`}
                </option>
              ))}
            </select>
          </ValueRow>
        </FormCard>
      </SheetBody>
    </>
  );
}
